import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Redirect } from "react-router-dom";
import Customers from "../../../../services/customers";

import { handle } from "../../../../common/responseHandler";
import CONSTANTS from "./../../../../common/constants";
import Confirmation from "../../../Common/Modals/Confirmation";
import ProlongedAction from "../../../Common/Modals/ProlongedAction";
import ChangeCultureDropdown from "./ChangeCultureDropdown";
import CustomersActions from "../../../../store/actions/customerActions";
import CulturesActions from "../../../../store/actions/cultureActions";
import _ from "lodash";
import { hasDealerRole } from '../../../../helpers/customers/dealerHelper';

function ActionBar({ customer }) {
	const dispatch = useDispatch();
	const userRole = useSelector(state => _.get(state, `user.isSuperUser`));
	const priorityCountries = useSelector(state => _.get(state, `environment.priorityCountries`));
	const [ modalIsOpen, setModalIsOpen ] = useState(false);
	const [ fireRedirect, setFireRedirect ] = useState(false);
	const [ actionInProgress, setActionsInProgress ] = useState(false);
	const [ changeCultureModalIsOpen, changeCultureSetModalIsOpen ] = useState(false);
	const [ changeCultureErrorModalIsOpen, changeCultureErrorSetModalIsOpen ] = useState(false);
	const [ cultureSelected, setCultureSelected ] = useState(null);
	const [ activateDealerRoleModalIsOpen, activateDealerRoleSetModalIsOpen ] = useState(false);
	const [ errorNoPrefDealerModalIsOpen, errorNoPrefDealerSetModalIsOpen ] = useState(false);
	const [ errorMaxLimitModalIsOpen, errorMaxLimitSetModalIsOpen ] = useState(false);
	const [ prolongedActionMessage, setProlongedActionMessage ] = useState({
		title: ``,
		message: ``
	});
	const [ isDealer, setIsDealer ] = useState(hasDealerRole(customer));
	const cultures = useSelector(state => _.get(state, `culture.allLanguages`, []));
	const preferredDealerAddress = _.get(customer, `preferredDealerAddress`, {});

	useEffect(() => {
		if (_.isEmpty(cultures)) {
			dispatch(CulturesActions.getAllLanguages()).catch(handle);
		}
		dispatch(CustomersActions.getPreferredDealer(customer.uuid, customer.brand));
		dispatch(CustomersActions.getGroups(customer.uuid)).catch(handle);
	}, [ cultures, dispatch, customer.uuid, customer.brand ]);

	useEffect(() => {
		setIsDealer(_.includes(_.get(customer, `groups`, []), `DEALER`));
	}, [customer]);

	function deleteCustomer(e) {
		e.preventDefault();
		setModalIsOpen(false);
		doAction(
			Customers.delete,
			{
				title: `Deleting customer account`,
				message: `Please wait while we delete all customer data from our systems...`
			},
			() => {
				setFireRedirect(true);
			}
		);
	}

	const handleCultureChange = (event) => {
		setCultureSelected(event.target.value);
	};

	async function changeCulture(e) {
		e.preventDefault();
		changeCultureSetModalIsOpen(false);
		await Customers.changeCulture(customer.uuid, cultureSelected);
		dispatch(CustomersActions.changeCulture(customer.uuid, cultureSelected));
	}

	async function canSetDealer() {
		try {
			await Customers.canSetDealer(customer);
			activateDealerRoleSetModalIsOpen(true);
		} catch (error) {
			if(error.status === CONSTANTS.STATUS_CODE.BAD_REQUEST) {
				errorMaxLimitSetModalIsOpen(true);
			}

			handle(error);
		}
	}

	async function activateDealerRole(e) {
		e.preventDefault();
		activateDealerRoleSetModalIsOpen(false);
		try {
			const result = await Customers.activateRole(customer, `DEALER`);
		    handle(result);
			dispatch(CustomersActions.activateDealerRole(customer));
		} catch (error) {
			handle(error);
		}

	}

	async function doAction(asyncAction, prolongedActionMessage, otherActions) {
		if (!actionInProgress) {
			setProlongedActionMessage(prolongedActionMessage);
			setActionsInProgress(true);

			try {
				const result = await asyncAction(customer);
				handle(result);
			} catch (error) {
				handle(error);
			} finally {
				setActionsInProgress(false);
				otherActions && otherActions();
			}
		}
	}

	function resendActivationMailButton() {
		return <button
			className="btn btn-warning margin-top-12"
			onClick={() => {
				doAction(Customers.resendActivationMail, {
					title: `Sending activation mail`,
					message: `Please wait while we contact our mailing service and look for the customer...`
				});
			}}
			disabled={actionInProgress || customer.hasVerifiedEmail}
			title={customer.hasVerifiedEmail
				? `Disabled, the email is already verified`
				: `Resend activation mail`}
		>
			<span className="glyphicon glyphicon-repeat" aria-hidden="true"/>
			{` `}
			Resend activation mail
		</button>;
	}

	function sendResetPasswordMailButton() {
		return <button
			className="btn btn-calculation margin-top-12 button-spacing-left-10"
			onClick={() => {
				doAction(Customers.resetPasswordMail, {
					title: `Sending reset password mail`,
					message: `Please wait while we contact our mailing service and look for the customer...`
				});
			}}
			disabled={actionInProgress || !customer.hasAcceptedTerms}
			title={!customer.hasAcceptedTerms
				? `Disabled, account not verified, resend activation instead`
				: `Sending reset password mail`}
		>
			<span className="fa fa-key" aria-hidden="true"/> Send reset password mail
		</button>;
	}

	function activateDealerRoleButton() {
		return <button
			className="btn btn-primary margin-top-12 button-spacing-left-10"
			onClick={() => {
				customer.preferredDealerId ? canSetDealer() : errorNoPrefDealerSetModalIsOpen(true);
			}}
			disabled={actionInProgress}
		>
			<span className="fa fa-plus" aria-hidden="true"/> Activate dealer role
		</button>;
	}

	function changeCultureButton() {
		return <button
			className="btn btn-primary margin-top-12 button-spacing-left-10"
			onClick={() => {
				!isDealer ? changeCultureSetModalIsOpen(true): changeCultureErrorSetModalIsOpen(true);
			}}
			disabled={actionInProgress}
		>
			<span className="fa fa-remove" aria-hidden="true"/> Change country
		</button>;
	}

	function deleteAccountButton() {
		return <button
			className="btn btn-calculation margin-top-12 button-spacing-left-10"
			onClick={() => {
				setModalIsOpen(true);
			}}
			disabled={actionInProgress}
		>
			<span className="fa fa-remove" aria-hidden="true"/> Delete account
		</button>;
	}

	return (
		<div className="row">
			<div className="col-xs-6">
				{resendActivationMailButton()}
				{sendResetPasswordMailButton()}
			</div>
			<div className="col-xs-6">
				<div className=" pull-right">
					{!isDealer && activateDealerRoleButton()}
					{userRole && !Object.values(priorityCountries).indexOf(customer.country) > -1
						? <>
							{changeCultureButton()}
						</>
						: null}
					{deleteAccountButton()}
				</div>
			</div>
			{fireRedirect && <Redirect to={`${CONSTANTS.ROUTE.CUSTOMERS}`}/>}
			<Confirmation
				isError={false}
				modalIsOpen={changeCultureModalIsOpen}
				setModalIsOpen={changeCultureSetModalIsOpen}
				title={`Change locale`}
				message={`Please select a new locale and confirm`}
				onAccept={changeCulture}
				onDecline={() => {
					changeCultureSetModalIsOpen(false);
				}}
			>
				<ChangeCultureDropdown onChange={handleCultureChange} value={cultureSelected}/>
			</Confirmation>

			<Confirmation
				isError
				modalIsOpen={changeCultureErrorModalIsOpen}
				setModalIsOpen={changeCultureErrorSetModalIsOpen}
				title={`Change country`}
				message={`The country can't be changed for an account for which the dealer role is active.`}
				onAccept={null}
				onDecline={() => {
					changeCultureErrorSetModalIsOpen(false);
				}}
			/>

			<Confirmation
				isError={false}
				modalIsOpen={modalIsOpen}
				setModalIsOpen={setModalIsOpen}
				title={`Delete customer`}
				message={`Are you sure you want to delete the account of the customer?`}
				onAccept={deleteCustomer}
				onDecline={() => {
					setModalIsOpen(false);
				}}
			>
				<span>
					This action is <strong>not reversible</strong>!
				</span>
			</Confirmation>

			<Confirmation
				isError={false}
				modalIsOpen={activateDealerRoleModalIsOpen}
				setModalIsOpen={activateDealerRoleSetModalIsOpen}
				title={`Activate dealer role`}
				message={`Are you sure you want to link the customer to its preferred dealer:`}
				onAccept={activateDealerRole}
				onDecline={() => {
					activateDealerRoleSetModalIsOpen(false);
				}}
			>
				<div>
					<br />
					<strong>{customer.preferredDealerName}</strong><br />
					{preferredDealerAddress.address1}, {preferredDealerAddress._city}<br />
					{preferredDealerAddress._region}, {preferredDealerAddress._zip}<br />
					<br />
					Notice: A dealer can add up to 50 cars to his account for all brands combined.
				</div>
			</Confirmation>

			<Confirmation
				isError
				modalIsOpen={errorNoPrefDealerModalIsOpen}
				setModalIsOpen={errorNoPrefDealerSetModalIsOpen}
				title={`Unable to assign dealer role`}
				message={`No preferred dealer set for this customer`}
				onAccept={null}
				onDecline={() => {
					errorNoPrefDealerSetModalIsOpen(false);
				}}
			/>

			<Confirmation
				isError
				modalIsOpen={errorMaxLimitModalIsOpen}
				setModalIsOpen={errorMaxLimitSetModalIsOpen}
				title={`Unable to assign dealer role`}
				message={`The selected dealer already has the maximum amount of customers linked to it`}
				onAccept={null}
				onDecline={() => {
					errorMaxLimitSetModalIsOpen(false);
				}}
			/>

			<ProlongedAction
				actionInProgress={actionInProgress}
				title={prolongedActionMessage.title}
				message={prolongedActionMessage.message}/>
		</div>
	);
}

ActionBar.propTypes = {
	customer: PropTypes.object
};

export default ActionBar;